'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { motion, useInView, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'

export type GalleryImage = {
  image: string
  alt?: string
}

type Direction = 'left' | 'right'

type PhotoLayout = {
  id: number
  order: number
  x: number
  y: number
  zIndex: number
  direction: Direction
  rotate: number
  src: string
  alt: string
}

const EASE = [0.16, 1, 0.3, 1] as const

function getSpacing(
  count: number,
  photoSize: number,
  wide: boolean,
  compact: boolean
): number {
  if (count <= 1) return 0
  // Space centers apart — higher ratio = less overlap between frames
  const ratio = compact ? 0.78 : wide ? 0.9 : 0.84
  const sizeBased = Math.round(photoSize * ratio)
  const countBased = Math.floor((compact ? 640 : wide ? 1100 : 880) / count)
  return Math.max(sizeBased, countBased)
}

function buildPhotoLayout(
  images: GalleryImage[],
  photoSize: number,
  wide = false,
  compact = false
): PhotoLayout[] {
  const count = images.length
  if (count === 0) return []

  const spacing = getSpacing(count, photoSize, wide, compact)
  const center = (count - 1) / 2
  const yOffsets = compact ? [4, 10, 2, 8, 12, 6, 10] : [6, 14, 3, 10, 18, 8, 12]
  const rotations = [-3, 2.5, -2, 3, -2.5, 2]

  return images.map((img, index) => ({
    id: index + 1,
    order: index,
    x: (index - center) * spacing,
    y: yOffsets[index % yOffsets.length],
    zIndex: 50 - Math.floor((index * 40) / Math.max(count, 1)),
    direction: index % 2 === 0 ? 'left' : 'right',
    rotate: rotations[index % rotations.length],
    src: img.image,
    alt: img.alt || `Gallery image ${index + 1}`,
  }))
}

export function PhotoGallery({
  images,
  animationDelay = 0.3,
  fullBleed = false,
  smoothEntrance = false,
}: {
  images: GalleryImage[]
  animationDelay?: number
  fullBleed?: boolean
  smoothEntrance?: boolean
}) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-5%' })
  const reducedMotion = useReducedMotion()
  const [photoSize, setPhotoSize] = useState(200)
  const [compact, setCompact] = useState(false)
  const [hoveredId, setHoveredId] = useState<number | null>(null)
  const [heroEntranceReady, setHeroEntranceReady] = useState(false)

  const useSmooth = smoothEntrance || fullBleed
  const shouldAnimate = useSmooth ? heroEntranceReady : isInView
  const [legacyVisible, setLegacyVisible] = useState(!useSmooth)
  const [legacyLoaded, setLegacyLoaded] = useState(!useSmooth)

  useEffect(() => {
    if (!useSmooth) return
    const timer = setTimeout(() => setHeroEntranceReady(true), 850)
    return () => clearTimeout(timer)
  }, [useSmooth])

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth
      setCompact(w < 640)
      setPhotoSize(w < 640 ? 128 : w < 1024 ? 168 : 200)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const photos = useMemo(
    () => buildPhotoLayout(images, photoSize, fullBleed, compact),
    [images, photoSize, fullBleed, compact]
  )

  useEffect(() => {
    if (useSmooth) return
    const visibilityTimer = setTimeout(() => setLegacyVisible(true), animationDelay * 1000)
    const animationTimer = setTimeout(
      () => setLegacyLoaded(true),
      (animationDelay + 0.4) * 1000
    )
    return () => {
      clearTimeout(visibilityTimer)
      clearTimeout(animationTimer)
    }
  }, [animationDelay, useSmooth])

  if (photos.length === 0) {
    return (
      <div className="flex h-48 w-full items-center justify-center bg-brand-green-soft">
        <span className="text-sm text-brand-muted">No gallery images yet</span>
      </div>
    )
  }

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reducedMotion ? 0 : 0.14,
        delayChildren: reducedMotion ? 0 : 0.12,
      },
    },
  }

  const photoVariants = {
    hidden: (custom: PhotoLayout) =>
      reducedMotion
        ? { opacity: 0 }
        : {
            x: custom.x,
            y: custom.y + 56,
            opacity: 0,
            scale: 0.94,
            rotate: custom.rotate,
          },
    visible: (custom: PhotoLayout) => ({
      x: custom.x,
      y: custom.y,
      opacity: 1,
      scale: 1,
      rotate: custom.rotate,
      transition: {
        type: 'tween' as const,
        duration: reducedMotion ? 0.35 : 0.95,
        ease: EASE,
        delay: custom.order * (reducedMotion ? 0.05 : 0.1),
      },
    }),
  }

  const showPhotos = useSmooth ? shouldAnimate : legacyLoaded
  const showWrapper = useSmooth ? shouldAnimate : legacyVisible

  return (
    <motion.div
      ref={sectionRef}
      className={cn(
        'relative w-full',
        fullBleed ? 'pt-10 pb-2 sm:py-4 overflow-x-auto overflow-y-visible sm:overflow-visible' : 'mt-8 overflow-visible'
      )}
      initial={false}
      animate={{ opacity: showWrapper ? 1 : 0 }}
      transition={{ duration: 0.4, ease: EASE }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-8 -z-10 mx-auto hidden h-[280px] max-w-5xl opacity-20 md:block bg-[linear-gradient(to_right,#14213d33_1px,transparent_1px),linear-gradient(to_bottom,#14213d33_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]"
        aria-hidden
      />

      <div
        className={cn(
          'relative flex w-full items-center justify-center',
          fullBleed
            ? 'min-h-[280px] sm:min-h-[320px] lg:min-h-[360px] overflow-x-auto overflow-y-visible sm:overflow-visible'
            : 'mb-8 h-[320px] sm:h-[350px] overflow-visible'
        )}
      >
        <div className="relative mx-auto flex w-full max-w-7xl justify-center px-4">
          <motion.div
            className="relative flex w-full justify-center"
            variants={containerVariants}
            initial="hidden"
            animate={showPhotos ? 'visible' : 'hidden'}
          >
            <motion.div
              className="relative overflow-visible"
              style={{ width: photoSize, height: photoSize }}
              animate={
                useSmooth && showPhotos && !reducedMotion
                  ? { y: [0, -4, 0] }
                  : undefined
              }
              transition={
                useSmooth && showPhotos && !reducedMotion
                  ? { duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }
                  : undefined
              }
            >
              {[...photos].reverse().map((photo) => (
                <motion.div
                  key={photo.id}
                  className="absolute left-0 top-0 will-change-transform"
                  style={{ zIndex: hoveredId === photo.id ? 100 : photo.zIndex }}
                  variants={photoVariants}
                  custom={photo}
                  onHoverStart={() => setHoveredId(photo.id)}
                  onHoverEnd={() => setHoveredId(null)}
                  whileHover={
                    reducedMotion
                      ? undefined
                      : {
                          y: photo.y - 10,
                          scale: 1.06,
                          transition: { type: 'spring', stiffness: 320, damping: 28 },
                        }
                  }
                >
                  <Photo
                    width={photoSize}
                    height={photoSize}
                    src={photo.src}
                    alt={photo.alt}
                    rotation={photo.rotate}
                    interactive={!useSmooth}
                  />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}

export const Photo = ({
  src,
  alt,
  className,
  rotation = 0,
  interactive = true,
  width,
  height,
}: {
  src: string
  alt: string
  className?: string
  rotation?: number
  interactive?: boolean
  width: number
  height: number
}) => {
  const [imageLoaded, setImageLoaded] = useState(true)

  return (
    <motion.div
      drag={interactive}
      dragConstraints={interactive ? { left: 0, right: 0, top: 0, bottom: 0 } : undefined}
      dragElastic={0.12}
      whileTap={interactive ? { scale: 1.04 } : undefined}
      whileDrag={interactive ? { scale: 1.04 } : undefined}
      style={{
        width,
        height,
        rotate: rotation,
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        touchAction: interactive ? 'none' : 'auto',
      }}
      className={cn(
        className,
        interactive
          ? 'group relative mx-auto shrink-0 cursor-grab active:cursor-grabbing'
          : 'group relative mx-auto shrink-0'
      )}
      draggable={false}
      tabIndex={interactive ? 0 : -1}
    >
      <div className="relative h-full w-full overflow-hidden rounded-2xl border border-white/80 bg-white shadow-[0_16px_48px_-20px_rgba(20,33,61,0.35)] transition-shadow duration-500 group-hover:shadow-[0_24px_56px_-16px_rgba(20,33,61,0.4)] sm:rounded-3xl">
        <Image
          className={cn(
            'rounded-2xl object-cover transition-opacity duration-700 ease-out sm:rounded-3xl',
            imageLoaded ? 'opacity-100' : 'opacity-0'
          )}
          fill
          sizes="(max-width: 640px) 160px, 220px"
          src={src}
          alt={alt}
          onLoad={() => setImageLoaded(true)}
          draggable={false}
        />
      </div>
    </motion.div>
  )
}
